/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

/**
 * Dashboard.js - Summary-Only Dashboard
 *
 * Phase 0 Task 0.6: Deduplication
 * - Dashboard shows ONLY summary cards with counts/brief info
 * - Each piece of content has ONE home (not here)
 * - Links to dedicated pages: Workspace (goals), Roadmap, Community, SkillBot
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  TrendingUp, Award, Book, Target, ArrowRight, Sparkles,
  Users, Briefcase, MessageSquare, Map, Archive, Link2, Terminal
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import TrackSelector from '../components/TrackSelector';
import LoadingSpinner from '../components/common/LoadingSpinner';

// Entity type display helpers
const getEntityLabel = (entityType) => {
  const labels = {
    individual: 'Personal Account',
    organization: 'Organization Account',
    university: 'University Account',
    institute: 'Institute Account'
  };
  return labels[entityType] || 'Account';
};

const getEntityIcon = (entityType) => {
  const icons = {
    individual: '👤',
    organization: '🏢',
    university: '🎓',
    institute: '📚'
  };
  return icons[entityType] || '👤';
};

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [resume, setResume] = useState(null);
  const [roadmap, setRoadmap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wikiSnippets, setWikiSnippets] = useState([]);
  const [wikiSnippetsLoading, setWikiSnippetsLoading] = useState(false);

  useEffect(() => {
    loadData();
    fetchWikiSnippets();
  }, []);

  const loadData = async () => {
    try {
      const resumeRes = await api.getResume(user.id);
      if (resumeRes.success) {
        setResume(resumeRes.data);
      }

      try {
        const roadmapRes = await api.getRoadmap(user.id, user.vertical);
        if (roadmapRes.success) {
          setRoadmap(roadmapRes.data);
        }
      } catch (err) {
        console.log('No roadmap found');
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWikiSnippets = async () => {
    setWikiSnippetsLoading(true);
    try {
      const domain = user?.vertical?.toLowerCase() || 'wms';
      const res = await api.get(`/wiki/feed/dashboard?domain=${domain}`);
      const entries = res.data?.entries || res.entries || [];
      setWikiSnippets(entries);
    } catch (e) {
      setWikiSnippets([]);
    } finally {
      setWikiSnippetsLoading(false);
    }
  };

  const handleTrackChange = (newTrack) => {
    user.vertical = newTrack;
    loadData();
  };

  const verticalInfo = {
    WMS: {
      title: 'Warehouse Management',
      icon: '🏭',
      color: 'from-cyan-400 to-blue-500',
    },
    iSeries: {
      title: 'IBM i / AS400',
      icon: '💻',
      color: 'from-blue-400 to-indigo-500',
    },
    Civil: {
      title: 'Civil Construction',
      icon: '🔨',
      color: 'from-orange-400 to-red-500',
    },
    Stitching: {
      title: 'Apparel & Textiles',
      icon: '👕',
      color: 'from-green-400 to-teal-500',
    },
    SkillCode: {
      title: 'AI Software Engineering',
      icon: '🖥️',
      color: 'from-violet-400 to-purple-500',
    },
    // ShubbhYatra Phase 1 — restricted vertical. Label shows for reps
    // whose vertical was set to 'Travel' via @shubbhyatra.com signup or
    // pending-invite consumption.
    Travel: {
      title: 'यात्रा / Travel',
      icon: '✈',
      color: 'from-sky-400 to-cyan-500',
    }
  };

  const currentVertical = verticalInfo[user.vertical] || verticalInfo.WMS;

  // Calculate summary stats
  const getTotalSkills = () => {
    if (!roadmap?.learningPath) return 0;
    return roadmap.learningPath.reduce((sum, level) => sum + (level.skills?.length || 0), 0);
  };

  const getRoadmapProgress = () => {
    if (!roadmap?.learningPath) return 0;
    return Math.min(65 + (roadmap.learningPath.length * 5), 95);
  };

  if (loading) return <LoadingSpinner message="Loading your dashboard..." />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-purple-50">
      {/* Hero Section */}
      <div className={`bg-gradient-to-r ${currentVertical.color} text-white px-8 py-10 rounded-b-3xl shadow-xl`}>
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <Sparkles className="w-7 h-7" />
            <h1 className="text-3xl font-bold">
              Welcome back{user.fullName ? `, ${user.fullName}` : user.name ? `, ${user.name}` : ''}!
            </h1>
          </div>
          <p className="text-lg opacity-90">Your personalized skill development hub for {currentVertical.title}</p>

          {/* User Info Badge */}
          <div className="flex flex-wrap items-center gap-3 mt-4">
            <div className="flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full backdrop-blur-sm">
              <span className="text-lg">{getEntityIcon(user.entityType)}</span>
              <span className="text-sm font-medium">{getEntityLabel(user.entityType)}</span>
            </div>
            {user.entityType !== 'individual' && user.workspaceName && (
              <div className="flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full backdrop-blur-sm">
                <Briefcase className="w-4 h-4" />
                <span className="text-sm font-medium">{user.workspaceName}</span>
              </div>
            )}
            <div className="flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full backdrop-blur-sm">
              <span className="text-sm font-medium">{user.vertical} Track</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Summary Cards Only */}
      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">

        {/* Track Selector */}
        <TrackSelector user={user} onTrackChange={handleTrackChange} />

        {/* Quick Summary Stats - Clickable Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <button
            onClick={() => navigate('/workspace')}
            className="bg-white rounded-xl p-5 shadow-lg hover:shadow-xl transition-all text-left group border-2 border-transparent hover:border-purple-300"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Target className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Active Goals</p>
                <p className="text-2xl font-bold text-gray-800">View All</p>
              </div>
            </div>
            <p className="text-xs text-purple-600 mt-2 flex items-center gap-1">
              Go to Workspace <ArrowRight className="w-3 h-3" />
            </p>
          </button>

          <button
            onClick={() => navigate('/roadmap')}
            className="bg-white rounded-xl p-5 shadow-lg hover:shadow-xl transition-all text-left group border-2 border-transparent hover:border-cyan-300"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Map className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Roadmap Progress</p>
                <p className="text-2xl font-bold text-gray-800">
                  {roadmap ? `${getRoadmapProgress()}%` : 'Start'}
                </p>
              </div>
            </div>
            <p className="text-xs text-cyan-600 mt-2 flex items-center gap-1">
              {roadmap ? 'View full roadmap' : 'Generate roadmap'} <ArrowRight className="w-3 h-3" />
            </p>
          </button>

          <button
            onClick={() => navigate('/learning')}
            className="bg-white rounded-xl p-5 shadow-lg hover:shadow-xl transition-all text-left group border-2 border-transparent hover:border-green-300"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Book className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Learning Skills</p>
                <p className="text-2xl font-bold text-gray-800">{getTotalSkills()}</p>
              </div>
            </div>
            <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
              Browse resources <ArrowRight className="w-3 h-3" />
            </p>
          </button>

          <button
            onClick={() => navigate('/profile')}
            className="bg-white rounded-xl p-5 shadow-lg hover:shadow-xl transition-all text-left group border-2 border-transparent hover:border-orange-300"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Award className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Profile Skills</p>
                <p className="text-2xl font-bold text-gray-800">{resume?.parsedData?.skills?.length || 0}</p>
              </div>
            </div>
            <p className="text-xs text-orange-600 mt-2 flex items-center gap-1">
              View profile <ArrowRight className="w-3 h-3" />
            </p>
          </button>
        </div>

        {/* Quick Navigation Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {/* Workspace Card */}
          <button
            onClick={() => navigate('/workspace')}
            className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all text-left group border-2 border-transparent hover:border-purple-300"
          >
            <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <Target className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-bold text-lg text-gray-800 mb-2">My Workspace</h3>
            <p className="text-sm text-gray-600 mb-3">Manage goals, track progress, and customize your dashboard</p>
            <span className="text-purple-600 text-sm font-medium flex items-center gap-1">
              Open Workspace <ArrowRight className="w-4 h-4" />
            </span>
          </button>

          {/* Roadmap Card */}
          <button
            onClick={() => navigate('/roadmap')}
            className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all text-left group border-2 border-transparent hover:border-cyan-300"
          >
            <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-bold text-lg text-gray-800 mb-2">Learning Roadmap</h3>
            <p className="text-sm text-gray-600 mb-3">
              {roadmap
                ? `${roadmap.experienceLevel} level • ${roadmap.learningPath?.length || 0} stages`
                : 'Generate your personalized learning path'}
            </p>
            <span className="text-cyan-600 text-sm font-medium flex items-center gap-1">
              {roadmap ? 'View Full Roadmap' : 'Generate Roadmap'} <ArrowRight className="w-4 h-4" />
            </span>
          </button>

          {/* SkillCode Card */}
          <button
            onClick={() => navigate('/skillcode')}
            className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all text-left group border-2 border-transparent hover:border-violet-300"
          >
            <div className="w-12 h-12 bg-gradient-to-br from-violet-400 to-purple-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <Terminal className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-bold text-lg text-gray-800 mb-2">SkillCode</h3>
            <p className="text-sm text-gray-600 mb-3">AI Software Engineering Intelligence</p>
            <span className="text-violet-600 text-sm font-medium flex items-center gap-1">
              Open SkillCode <ArrowRight className="w-4 h-4" />
            </span>
          </button>

          {/* Community Card */}
          <button
            onClick={() => navigate('/community')}
            className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all text-left group border-2 border-transparent hover:border-blue-300"
          >
            <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <Users className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-bold text-lg text-gray-800 mb-2">Community</h3>
            <p className="text-sm text-gray-600 mb-3">Connect with mentors, peers, and opportunities</p>
            <span className="text-blue-600 text-sm font-medium flex items-center gap-1">
              Explore Community <ArrowRight className="w-4 h-4" />
            </span>
          </button>
        </div>

        {/* Secondary Navigation */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <button
            onClick={() => navigate('/career-graph')}
            className="bg-white rounded-xl p-4 shadow hover:shadow-md transition-all text-left group flex items-center gap-3"
          >
            <div className="w-10 h-10 bg-gradient-to-br from-pink-400 to-purple-500 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-800">Career Graph</h4>
              <p className="text-xs text-gray-500">Visualize connections</p>
            </div>
          </button>

          <button
            onClick={() => navigate('/learning')}
            className="bg-white rounded-xl p-4 shadow hover:shadow-md transition-all text-left group flex items-center gap-3"
          >
            <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-teal-500 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
              <Book className="w-5 h-5 text-white" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-800">Learning Hub</h4>
              <p className="text-xs text-gray-500">Courses & resources</p>
            </div>
          </button>

          <button
            onClick={() => navigate('/knowledge-base')}
            className="bg-white rounded-xl p-4 shadow hover:shadow-md transition-all text-left group flex items-center gap-3"
          >
            <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
              <Archive className="w-5 h-5 text-white" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-800">Knowledge Base</h4>
              <p className="text-xs text-gray-500">Documents & guides</p>
            </div>
          </button>

          <button
            onClick={() => {/* SkillBot is floating, just highlight it */}}
            className="bg-gradient-to-r from-cyan-500 to-blue-500 rounded-xl p-4 shadow hover:shadow-md transition-all text-left group flex items-center gap-3"
          >
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <div>
              <h4 className="font-semibold text-white">Ask SkillBot</h4>
              <p className="text-xs text-white/80">AI assistant (bottom right)</p>
            </div>
          </button>
        </div>

        {/* Domain Intelligence Widget */}
        <div style={{ marginTop: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
              🔍 Domain Intelligence
            </h2>
            <a href="/knowledge-base" style={{ fontSize: '13px', color: '#7c3aed', textDecoration: 'none' }}>
              View all in Knowledge Base →
            </a>
          </div>

          {wikiSnippetsLoading ? (
            <div style={{ color: '#94a3b8', fontSize: '14px' }}>Loading domain updates...</div>
          ) : wikiSnippets.length === 0 ? (
            <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '20px', textAlign: 'center', color: '#94a3b8' }}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>🔍</div>
              <p style={{ margin: 0, fontSize: '14px' }}>
                Scout Agent runs daily at 6 AM IST. Check back tomorrow for fresh {user?.vertical} insights.
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
              {wikiSnippets.map(item => (
                <div key={item.id} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'flex-start' }}>
                    <span style={{ fontSize: '16px', marginTop: '2px' }}>
                      {item.source_type === 'scout_agent' ? '🔍' : item.source_type === 'seal_auto' ? '🤖' : '📋'}
                    </span>
                    <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600', lineHeight: '1.4', color: '#1e293b' }}>
                      {item.topic}
                    </h3>
                  </div>
                  <p style={{ margin: '0 0 12px', color: '#64748b', fontSize: '13px', lineHeight: '1.5', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {item.summary || item.content?.substring(0, 100)}
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                      {new Date(item.created_at).toLocaleDateString()}
                    </span>
                    {item.source_metadata?.url && (
                      <a href={item.source_metadata.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: '#7c3aed', textDecoration: 'none' }}>
                        Read more →
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* CTA for users without roadmap */}
        {!roadmap && (
          <div className="bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl shadow-xl p-8 text-white text-center">
            <Sparkles className="w-14 h-14 mx-auto mb-4 animate-pulse" />
            <h2 className="text-2xl font-bold mb-2">Ready to Start Your Journey?</h2>
            <p className="text-lg mb-6 opacity-90">
              Generate your personalized learning roadmap tailored to your {currentVertical.title} expertise
            </p>
            <button
              onClick={() => navigate('/roadmap')}
              className="px-8 py-3 bg-white text-cyan-600 rounded-xl font-bold text-lg hover:bg-gray-100 transition-colors shadow-xl"
            >
              Generate My Roadmap
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
